import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import Analytics from '../../../utils/analytics'
import AphmAnomaliesTable from './AphmAnomaliesTable'
import AphmAnomalyDrawer from './AphmAnomalyDrawer'
import AphmAnomalyHandleModal from './AphmAnomalyHandleModal'
import DefaultContainer from '../../../HOC/DefaultContainer'
import LOCAL_STORAGE_KEYS from '../../../utils/localStorage'
import { DEFAULT_APHM_ANOMALY_FILTERS } from './AphmAnomalyFilters'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { OrderBy, OrderDirections } from '../../../utils/constants'
import { hasUnseenAnomalies, Statuses } from '../../../utils/entities/aphmAnomaly'
import { TableParameters } from '../../../utils/entities/tableParameters'
import { getAllAphmAnomalies, getAphmAnomaliesSummary, getAphmAnomaly, handleAphmAnomaly } from '../../../utils/api/aphmAnomaly'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { retrieveMetadata } from '../../../utils/http'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { useLocalStorage } from '../../../hooks/UseLocalStorage'

const AphmAnomaliesView = () => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const { aphmAnomaliesSummary, aphmAnomaliesSeenUntil, markAphmAnomaliesSeen, setAphmAnomaliesSummary } = useContext(GlobalContext)

  const [anomalies, setAnomalies] = useState({ data: [], loading: false, metadata: null })
  const [detail, setDetail] = useState({ data: null, loading: false })
  const [decision, setDecision] = useState({ anomaly: null, status: null, loading: false })

  const [rawParameters, setParameters] = useLocalStorage(
    LOCAL_STORAGE_KEYS.APHM_ANOMALIES_VIEW.LIST_PARAMETERS,
    new TableParameters({
      order: { by: OrderBy.CREATED_AT, direction: OrderDirections.DESC },
      pageSize: 20,
      filters: { ...DEFAULT_APHM_ANOMALY_FILTERS }
    })
  )

  const parameters = useMemo(
    () => new TableParameters({ ...rawParameters, metadata: anomalies.metadata }),
    [rawParameters, anomalies.metadata]
  )

  const fetchAnomalies = useCallback(parameters => {
    setAnomalies(prev => ({ ...prev, loading: true }))

    getAllAphmAnomalies(user, parameters.toQueryParameters()).then(json => {
      setAnomalies({ data: json?.data ?? [], loading: false, metadata: retrieveMetadata(json?.meta) })
    }).catch(() => {
      setAnomalies(prev => ({ ...prev, loading: false }))
      onError(t('aphm_anomaly.load_error'))
    })
  }, [user?.id, t])

  useEffect(() => Analytics.pageView('/aphm-anomalies'), [])

  useEffect(() => {
    fetchAnomalies(new TableParameters({ ...rawParameters }))
  }, [fetchAnomalies, rawParameters])

  const refreshBadge = useCallback(() => {
    getAphmAnomaliesSummary(user).then(json => {
      if (json?.data) { setAphmAnomaliesSummary({ ...json.data }) }
    })
  }, [user?.id, setAphmAnomaliesSummary])

  const handleShowDetail = useCallback(anomaly => {
    setDetail({ data: anomaly, loading: true })

    getAphmAnomaly(user, anomaly).then(json => {
      setDetail({ data: json?.data ?? anomaly, loading: false })
    }).catch(() => {
      setDetail({ data: anomaly, loading: false })
      onError(t('aphm_anomaly.load_error'))
    })
  }, [user?.id, t])

  const handleDecision = useCallback(comment => {
    const { anomaly, status } = decision

    setDecision(prev => ({ ...prev, loading: true }))

    handleAphmAnomaly(user, anomaly, { status, comment }).then(json => {
      if (!json?.data) {
        onError(t('aphm_anomaly.actions.error'))

        return
      }

      onSuccess(t('aphm_anomaly.actions.success'))
      setDecision({ anomaly: null, status: null, loading: false })
      fetchAnomalies(new TableParameters({ ...rawParameters }))
      refreshBadge()
    }).catch(() => {
      onError(t('aphm_anomaly.actions.error'))
    }).finally(() => {
      setDecision(prev => ({ ...prev, loading: false }))
    })
  }, [decision, user, t, fetchAnomalies, rawParameters, refreshBadge])

  const handleAction = useCallback((anomaly, status) => {
    if (status === Statuses.OPEN) {
      setDecision({ anomaly, status, loading: true })

      handleAphmAnomaly(user, anomaly, { status }).then(json => {
        if (json?.data) {
          onSuccess(t('aphm_anomaly.actions.success'))
          fetchAnomalies(new TableParameters({ ...rawParameters }))
          refreshBadge()
        } else {
          onError(t('aphm_anomaly.actions.error'))
        }
      }).catch(() => {
        onError(t('aphm_anomaly.actions.error'))
      }).finally(() => {
        setDecision({ anomaly: null, status: null, loading: false })
      })

      return
    }

    setDecision({ anomaly, status, loading: false })
  }, [user, t, fetchAnomalies, rawParameters, refreshBadge])

  useEffect(() => { refreshBadge() }, [refreshBadge])

  const hasUnseen = useMemo(
    () => hasUnseenAnomalies(aphmAnomaliesSummary, aphmAnomaliesSeenUntil),
    [aphmAnomaliesSummary, aphmAnomaliesSeenUntil]
  )

  const handleResetFilters = useCallback(() => setParameters(prev => new TableParameters({
    ...prev, filters: { ...prev.filters, ...DEFAULT_APHM_ANOMALY_FILTERS }, page: 1
  })), [setParameters])

  return (
    <DefaultContainer>
      <AphmAnomaliesTable
        data={anomalies.data}
        loading={anomalies.loading}
        parameters={parameters}
        onFiltersChange={filters => setParameters(prev => new TableParameters({ ...prev, filters, page: 1 }))}
        onParametersChange={parameters => setParameters(prev => new TableParameters({ ...prev, ...parameters }))}
        onResetFilters={handleResetFilters}
        onHandle={handleAction}
        onMarkSeen={markAphmAnomaliesSeen}
        onShowDetail={handleShowDetail}
        hasUnseen={hasUnseen}
      />

      <AphmAnomalyDrawer
        anomaly={detail.data}
        loading={detail.loading}
        onClose={() => setDetail({ data: null, loading: false })}
      />

      <AphmAnomalyHandleModal
        anomaly={decision.anomaly}
        status={decision.status === Statuses.OPEN ? null : decision.status}
        loading={decision.loading}
        onCancel={() => setDecision({ anomaly: null, status: null, loading: false })}
        onConfirm={handleDecision}
      />
    </DefaultContainer>
  )
}

export default AphmAnomaliesView
